import { publicAxios as axios } from "../../../config/axios";
import { UseToastOptions } from "@chakra-ui/react";
import endpoints from "../../../utils/endpoints";

export interface BroadcastSupplier {
  supplierId: string;
  supplierName: string;
  mobileNumber: string;
  email?: string;
}

export interface SuprsendPerEntityStatus {
  sentAt?: string | null;
  respondedAt?: string | null;
  expiresAt?: string | null;
  status?: string | null;
}

export interface SuprsendRequestStatus {
  perEntityStatus?: Record<string, SuprsendPerEntityStatus>;
}

export enum BroadcastIntent {
  SEND_PART_AVAILABILITY_REQUEST_TO_SUPPLIER = "SEND_PART_AVAILABILITY_REQUEST_TO_SUPPLIER",
}

export const buildBroadcastRequestId = (orderNo: string) =>
  `${BroadcastIntent.SEND_PART_AVAILABILITY_REQUEST_TO_SUPPLIER}-enquiryNo:${orderNo}`;

interface SuprsendPublicRequest {
  entityId: string;
  sentAt?: string | null;
  respondedAt?: string | null;
  expiresAt?: string | null;
  status?: string | null;
}

interface SuprsendApiResponse {
  expiresAt?: string | null;
  publicRequests?: SuprsendPublicRequest[];
}

export enum BROADCAST_STATUS {
  RESPONDED = "RESPONDED",
  EXPIRED = "EXPIRED",
  SENT = "SENT",
}

function deriveEntityStatus(entity: SuprsendPublicRequest): string {
  if (entity.status) return entity.status;
  if (entity.respondedAt) return BROADCAST_STATUS.RESPONDED;

  const expirationDate = entity.expiresAt;
  const isExpired =
    expirationDate && !Number.isNaN(Date.parse(expirationDate))
      ? new Date(expirationDate) < new Date()
      : false;

  return isExpired ? BROADCAST_STATUS.EXPIRED : BROADCAST_STATUS.SENT;
}

export async function getNotificationRequestStatus(
  requestId: string,
): Promise<SuprsendRequestStatus | null> {
  if (!requestId) return null;

  try {
    const { data } = await axios.get(
      endpoints.getNotificationRequestStatus(requestId),
      {
        headers: {
          Authorization: localStorage.getItem("auth_orca"),
        },
      },
    );

    const root: SuprsendApiResponse = data?.data ?? data;

    if (!Array.isArray(root?.publicRequests)) {
      return { perEntityStatus: {} };
    }

    const perEntityStatus = root.publicRequests.reduce<
      Record<string, SuprsendPerEntityStatus>
    >((acc, entity) => {
      if (!entity.entityId) return acc;

      const entityId = String(entity.entityId);
      const newEntry = {
        sentAt: entity.sentAt || null,
        respondedAt: entity.respondedAt || null,
        expiresAt: entity.expiresAt || null,
        status: deriveEntityStatus(entity),
      };

      const existing = acc[entityId];
      if (existing) {
        const existingSentAt = existing.sentAt
          ? new Date(existing.sentAt).getTime()
          : 0;
        const newSentAt = newEntry.sentAt
          ? new Date(newEntry.sentAt).getTime()
          : 0;

        if (newSentAt > existingSentAt) {
          acc[entityId] = newEntry;
        }
      } else {
        acc[entityId] = newEntry;
      }

      return acc;
    }, {});

    return { perEntityStatus };
  } catch (error) {
    console.error("Error fetching suprsend request status:", error);
    return null;
  }
}

export async function sendBroadcastQuery(
  suppliers: BroadcastSupplier[],
  toast: (options: UseToastOptions) => void,
  orderNo: string,
  mobileConfigs?: any,
): Promise<void> {
  const validSuppliers = suppliers.filter((s) => s.mobileNumber?.trim());

  if (validSuppliers.length === 0) {
    toast({
      title:
        suppliers.length === 0
          ? "No Suppliers Selected"
          : "No Valid Mobile Numbers",
      description:
        suppliers.length === 0
          ? "Please add suppliers to the broadcast list first."
          : "None of the selected suppliers have valid mobile numbers.",
      status: suppliers.length === 0 ? "warning" : "error",
      duration: 4000,
      isClosable: true,
      position: "top",
    });
    return;
  }

  try {
    const supplierRecipients = validSuppliers.map((supplier) => {
      const recipient: any = {
        $whatsapp: [
          supplier.mobileNumber.startsWith("+")
            ? supplier.mobileNumber
            : `+${supplier.mobileNumber}`,
        ],
        is_transient: true,
        data: {
          supplierName: supplier.supplierName,
        },
      };

      if (supplier.email) {
        recipient.$email = [supplier.email];
      }

      return recipient;
    });

    const defaultBroadcastRecipients =
      mobileConfigs?.default_broadcast_recipient;

    const readOnlyEntityIds: string[] = [];

    if (
      Array.isArray(defaultBroadcastRecipients) &&
      defaultBroadcastRecipients.length > 0
    ) {
      defaultBroadcastRecipients.forEach((defaultRecipient) => {
        const {
          phone: defaultPhone,
          email: defaultEmail,
          name,
        } = defaultRecipient;

        if (!defaultPhone && !defaultEmail) return;

        const recipient: any = {
          is_transient: true,
          data: {
            supplierName: name || "Default Recipient",
          },
        };

        if (defaultPhone) {
          recipient.$whatsapp = [
            defaultPhone.startsWith("+") ? defaultPhone : `+${defaultPhone}`,
          ];
          readOnlyEntityIds.push(defaultPhone);
        }

        if (defaultEmail) {
          recipient.$email = [defaultEmail];
        }

        supplierRecipients.push(recipient);
      });
    }

    const payload = {
      intent: BroadcastIntent.SEND_PART_AVAILABILITY_REQUEST_TO_SUPPLIER,
      intentData: {},
      recipients: supplierRecipients,
      entityIds: [
        ...validSuppliers.map((s) => s.supplierId),
        ...readOnlyEntityIds,
      ],
      requestId: buildBroadcastRequestId(orderNo),
      readOnlyEntities: readOnlyEntityIds,
    };

    await axios.post(endpoints.sendBroadcast, payload, {
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });

    toast({
      title: "Broadcast Sent Successfully",
      description: `Query sent to ${validSuppliers.length} supplier(s).`,
      status: "success",
      duration: 4000,
      isClosable: true,
      position: "top",
    });
  } catch (error: any) {
    console.error("Error sending broadcast:", error);
    toast({
      title: "Broadcast Failed",
      description:
        error.response?.data?.message ||
        error.message ||
        "Failed to send broadcast",
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
    throw error;
  }
}
